<template>
  <div class="flex flex-col flex-grow bg-white w-full min-h-[400px] border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] p-8 rounded-[5px]">
    <div class="flex flex-col w-full">
      <div class="flex justify-between mb-4">
        <h1 class="text-xl text-black font-semibold">
          {{ project.domain }}
        </h1>

        <div class="flex items-center gap-x-2">
          <b-dropdown
            v-if="canUpdateDomainManagement"
            variant="secondary"
            class="h-[40px] focus:bg-[#F9F9F9]"
          >
            <template #button-content>
              Add Record Preset
            </template>
            <b-dropdown-item @click="addItemPreset('dmarc')">
              DMARC Record
            </b-dropdown-item>
          </b-dropdown>

          <b-btn
            v-if="canUpdateDomainManagement"
            variant="primary"
            class="h-[40px]"
            @click="addItem"
          >
            Add Record
          </b-btn>
        </div>
      </div>

      <div
        v-if="loading"
        class="w-full h-full mt-[50px] flex items-center justify-center"
      >
        <b-spinner
          class="ml-2"
          variant="primary"
          size="xl"
        />
      </div>

      <div
        v-else
        class="border w-[600px] min-w-full border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] rounded min-h-[150px]"
      >
        <div class="overflow-x-auto">
          <table class="border-collapse table-auto min-w-full text-base">
            <thead>
              <tr>
                <th
                  v-for="(item, index) in tableHead"
                  :key="index"
                  class="h-[40px] bg-[#F9F9F9] first:!pl-5 last:!pr-5 px-2 md:px-[20px] py-[10px] truncate overflow-hidden text-ellipsis text-sm md:text-base"
                >
                  <button
                    v-if="item"
                    class="w-full flex items-center justify-between p-0 text-[#6E747A] hover:text-black transition-colors"
                    :class="{'text-black': sortBy === item}"
                    @click="toggleSort(item)"
                  >
                    {{ item }}

                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-chevrons-up-down-icon ml-1 h-[14px] w-[14px]"
                    >
                      <path
                        d="m7 15 5 5 5-5"
                        :class="sortBy === item && sortDesc ? 'text-black' : 'text-[#6E747A]'"
                      />
                      <path
                        d="m7 9 5-5 5 5"
                        :class="sortBy === item && !sortDesc ? 'text-black' : 'text-[#6E747A]'"
                      />
                    </svg>
                  </button>
                  <div v-else>
                    {{ item }}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr
                v-for="item in records"
                :key="item.id"
                class="border-t border-[#D7D7D7]"
              >
                <td class="px-3 py-2 w-[150px] break-words">
                  <div
                    :class="[
                      'bg-[#ECF3FF] w-fit h-[28px] px-2 rounded text-[#4F83E3] text-sm font-medium uppercase flex items-center',
                      {'bg-red-50 text-red-400': [0, 1, 'A', 'AAAA'].includes(item.Type) },
                      {'bg-gray-100 text-gray-500': [3, 8, 'TXT', 'SRV'].includes(item.Type) },
                      {'bg-green-50 text-green-400': [5, 'RDR'].includes(item.Type) },
                      {'bg-orange-50 text-orange-400': [9, 10, 'CAA', 'PTR'].includes(item.Type) }
                    ]"
                  >
                    {{ awsConnected ? item.Type : allTypes[item.Type]?.name }}
                  </div>
                </td>
                <td class="px-3 py-2 break-words max-w-[160px] sm:max-w-[200px] md:max-w-[400px] xl:max-w-[600px]">
                  <template v-if="item.Value === 'd094fdac-8.b-cdn.net' && !item.Name">
                    {{ project.domain }}
                  </template>
                  <template v-else>
                    {{ item.Name }}
                  </template>
                </td>
                <td class="px-3 py-2 break-words max-w-[160px] sm:max-w-[200px] md:max-w-[400px] xl:max-w-[600px]">
                  {{ item.Value }}
                </td>
                <td class="px-3 py-2 break-words text-right">
                  {{ item.Weight ? item.Weight : null }}
                </td>
                <td class="px-3 py-2 break-words text-right">
                  <div
                    v-if="[5].includes(item.Type)"
                    class="bg-orange-50 w-fit h-[24px] px-[12px] rounded text-orange-400 text-xs font-semibold uppercase flex items-center"
                  >
                    Auto
                  </div>
                  <template v-else>
                    {{ allTtl[item.Ttl] }}
                  </template>
                </td>
                <td class="px-3 py-2 flex justify-end truncate overflow-visible text-ellipsis">
                  <b-dropdown
                    v-if="canUpdateDomainManagement"
                    toggle-class="p-0 bg-transparent border-0 w-[24px] h-[24px]"
                    menu-class="bg-white p-0"
                    right
                  >
                    <template #button-content>
                      <div class="py-2">
                        <i class="fa fa-ellipsis-h" />
                      </div>
                    </template>

                    <b-dropdown-item @click="editItem(item)">
                      <div class="flex items-center gap-[6px]">
                        <svg
                          width="18"
                          height="19"
                          viewBox="0 0 18 19"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="w-[16px] h-[16px]"
                        >
                          <path
                            d="M9 11.75C10.2426 11.75 11.25 10.7426 11.25 9.5C11.25 8.25736 10.2426 7.25 9 7.25C7.75736 7.25 6.75 8.25736 6.75 9.5C6.75 10.7426 7.75736 11.75 9 11.75Z"
                            stroke="currentColor"
                            stroke-width="1.2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M14.0455 11.5455C13.9547 11.7511 13.9276 11.9792 13.9677 12.2004C14.0078 12.4216 14.1133 12.6257 14.2705 12.7864L14.3114 12.8273C14.4382 12.9539 14.5387 13.1043 14.6074 13.2699C14.676 13.4354 14.7113 13.6128 14.7113 13.792C14.7113 13.9712 14.676 14.1487 14.6074 14.3142C14.5387 14.4798 14.4382 14.6302 14.3114 14.7568C14.1847 14.8836 14.0343 14.9842 13.8688 15.0528C13.7032 15.1214 13.5258 15.1568 13.3466 15.1568C13.1674 15.1568 12.9899 15.1214 12.8244 15.0528C12.6589 14.9842 12.5085 14.8836 12.3818 14.7568L12.3409 14.7159C12.1802 14.5587 11.9761 14.4533 11.7549 14.4132C11.5338 14.3731 11.3056 14.4001 11.1 14.4909C10.8983 14.5773 10.7264 14.7208 10.6052 14.9038C10.4841 15.0867 10.4191 15.3011 10.4182 15.5205V15.6364C10.4182 15.998 10.2745 16.3449 10.0188 16.6006C9.76305 16.8563 9.4162 17 9.05455 17C8.69289 17 8.34604 16.8563 8.09031 16.6006C7.83458 16.3449 7.69091 15.998 7.69091 15.6364V15.575C7.68563 15.3493 7.61258 15.1305 7.48126 14.9468C7.34993 14.7632 7.16641 14.6234 6.95455 14.5455C6.7489 14.4547 6.52078 14.4276 6.2996 14.4677C6.07842 14.5078 5.87433 14.6133 5.71364 14.7705L5.67273 14.8114C5.54608 14.9382 5.39569 15.0387 5.23015 15.1074C5.0646 15.176 4.88716 15.2113 4.70795 15.2113C4.52875 15.2113 4.35131 15.176 4.18576 15.1074C4.02022 15.0387 3.86983 14.9382 3.74318 14.8114C3.6164 14.6847 3.51582 14.5343 3.44719 14.3688C3.37857 14.2032 3.34325 14.0258 3.34325 13.8466C3.34325 13.6674 3.37857 13.4899 3.44719 13.3244C3.51582 13.1589 3.6164 13.0085 3.74318 12.8818L3.78409 12.8409C3.94128 12.6802 4.04672 12.4761 4.08682 12.2549C4.12693 12.0338 4.09985 11.8056 4.00909 11.6C3.92266 11.3983 3.77915 11.2264 3.59623 11.1052C3.4133 10.9841 3.19895 10.9191 2.97955 10.9182H2.86364C2.50198 10.9182 2.15513 10.7745 1.8994 10.5188C1.64367 10.2631 1.5 9.9162 1.5 9.55455C1.5 9.19289 1.64367 8.84604 1.8994 8.59031C2.15513 8.33458 2.50198 8.19091 2.86364 8.19091H2.925C3.15068 8.18563 3.36955 8.11258 3.55316 7.98126C3.73677 7.84993 3.87662 7.66641 3.95455 7.45455C4.04531 7.2489 4.07238 7.02078 4.03228 6.7996C3.99217 6.57842 3.88673 6.37433 3.72955 6.21364L3.68864 6.17273C3.56185 6.04608 3.46127 5.89569 3.39265 5.73015C3.32402 5.5646 3.2887 5.38716 3.2887 5.20795C3.2887 5.02875 3.32402 4.85131 3.39265 4.68576C3.46127 4.52022 3.56185 4.36983 3.68864 4.24318C3.81528 4.1164 3.96568 4.01582 4.13122 3.94719C4.29676 3.87857 4.47421 3.84325 4.65341 3.84325C4.83261 3.84325 5.01006 3.87857 5.1756 3.94719C5.34114 4.01582 5.49154 4.1164 5.61818 4.24318L5.65909 4.28409C5.81978 4.44128 6.02387 4.54672 6.24505 4.58682C6.46623 4.62693 6.69435 4.59985 6.9 4.50909H6.95455C7.15621 4.42266 7.32819 4.27915 7.44934 4.09623C7.57048 3.9133 7.63549 3.69895 7.63636 3.47955V3.36364C7.63636 3.00198 7.78003 2.65513 8.03576 2.3994C8.2915 2.14367 8.63834 2 9 2C9.36166 2 9.7085 2.14367 9.96424 2.3994C10.22 2.65513 10.3636 3.00198 10.3636 3.36364V3.425C10.3645 3.6444 10.4295 3.85876 10.5507 4.04168C10.6718 4.22461 10.8438 4.36812 11.0455 4.45455C11.2511 4.54531 11.4792 4.57238 11.7004 4.53228C11.9216 4.49217 12.1257 4.38673 12.2864 4.22955L12.3273 4.18864C12.4539 4.06185 12.6043 3.96127 12.7699 3.89265C12.9354 3.82402 13.1128 3.7887 13.292 3.7887C13.4712 3.7887 13.6487 3.82402 13.8142 3.89265C13.9798 3.96127 14.1302 4.06185 14.2568 4.18864C14.3836 4.31528 14.4842 4.46568 14.5528 4.63122C14.6214 4.79676 14.6568 4.97421 14.6568 5.15341C14.6568 5.33261 14.6214 5.51006 14.5528 5.6756C14.4842 5.84114 14.3836 5.99154 14.2568 6.11818L14.2159 6.15909C14.0587 6.31978 13.9533 6.52387 13.9132 6.74505C13.8731 6.96623 13.9001 7.19435 13.9909 7.4V7.45455C14.0773 7.65621 14.2208 7.82819 14.4038 7.94934C14.5867 8.07048 14.8011 8.13549 15.0205 8.13636H15.1364C15.498 8.13636 15.8449 8.28003 16.1006 8.53576C16.3563 8.7915 16.5 9.13834 16.5 9.5C16.5 9.86166 16.3563 10.2085 16.1006 10.4642C15.8449 10.72 15.498 10.8636 15.1364 10.8636H15.075C14.8556 10.8645 14.6412 10.9295 14.4583 11.0507C14.2754 11.1718 14.1319 11.3438 14.0455 11.5455Z"
                            stroke="currentColor"
                            stroke-width="1.2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        Edit
                      </div>
                    </b-dropdown-item>
                    <delete-confirm
                      title="Are you sure?"
                      description="The DNS record will be deleted, the action cannot be undone."
                      @confirm="deleteRecord(item)"
                    >
                      <template #activator="{on}">
                        <b-dropdown-item v-on="on">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              width="18"
                              height="19"
                              viewBox="0 0 18 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              class="w-[16px] h-[16px]"
                            >
                              <path
                                d="M6.74988 2.75H11.2499M2.24988 5H15.7499M14.2499 5L13.7239 12.8895C13.645 14.0732 13.6055 14.665 13.3499 15.1138C13.1248 15.5088 12.7853 15.8265 12.3761 16.0248C11.9114 16.25 11.3182 16.25 10.1319 16.25H7.86787C6.68156 16.25 6.0884 16.25 5.62363 16.0248C5.21445 15.8265 4.87494 15.5088 4.64987 15.1138C4.39421 14.665 4.35475 14.0732 4.27584 12.8895L3.74988 5M7.49988 8.375V12.125M10.4999 8.375V12.125"
                                stroke="currentColor"
                                stroke-width="1.2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                            Delete
                          </div>
                        </b-dropdown-item>
                      </template>
                    </delete-confirm>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="flex justify-between items-center p-4 border-t border-[#D7D7D7]">
          <div class="text-sm text-gray-600">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min((currentPage - 1) * pageSize + pageSize, totalRecords) }} of {{ totalRecords }} records
          </div>

          <div class="flex gap-2">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="pageSize"
              class="mb-0"
              @input="onPageChange"
            />
          </div>
        </div>
      </div>
      
      <DnsPresetModal
        v-model="presetModal"
        :form="form"
        :preset="preset"
        :loading="loading"
        @submit="addRecord"
      />

      <DnsRecordModal
        v-model="modal"
        :form="form"
        :loading="loading"
        :domain="project.domain"
        :aws="awsConnected"
        @addRecord="addRecord"
        @updateRecord="updateRecord"
      />
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import DeleteConfirm from '@/components/editor/modals/DeleteConfirm.vue'
import DnsRecordModal from '@/views/domain/modals/DnsRecordModal.vue'
import DnsPresetModal from '@/views/domain/modals/DnsPresetModal.vue'
export default {
  name: 'DomainDNSRecord',
  components: {
    DeleteConfirm,
    DnsRecordModal,
    DnsPresetModal
  },
  data () {
    return {
      newAPI: 'https://new-api.estage.com',
      loading: false,
      modal: false,
      presetModal: false,
      preset: '',
      records: [],
      sortDesc: false,
      sortBy: 'Type',
      currentPage: 1,
      totalRecords: 0,
      pageSize: 20,
      awsDnsMigrated: false,
      tableHead: [
        'Type',
        'Name',
        'Value', 
        'Weight',
        'TTL',
        ''
      ],
      allTypes: [
        {
          name: 'A',
          value: 0,
          field: {
            name: 'IPv4 Address',
            placeholder: 'Example: 192.0.2.55'
          }
        },
        {
          name: 'AAAA',
          value: 1,
          field: {
            name: 'IPv6 Address',
            placeholder: 'Example: 2001:0db8:85a3:0000:0000:8a2e:0370:7334'
          }
        },
        {
          name: 'CNAME',
          value: 2,
          field: {
            name: 'Target',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'TXT',
          value: 3,
          field: {
            name: 'Content',
            placeholder: 'Example Text Entry'
          }
        },
        {
          name: 'MX',
          value: 4,
          field: {
            name: 'Mail Server',
            placeholder: 'mailserver.example.com'
          }
        },
        {
          name: 'RDR',
          value: 5,
          field: {
            name: 'URL',
            placeholder: 'https://mywebsite.com'
          }
        },
        {
          name: 'Flatten',
          value: 6
        },
        {
          name: 'PZ',
          value: 7
        },
        {
          name: 'SRV',
          value: 8,
          field: {
            name: 'Server Hostname',
            placeholder: 'xmpp-server.example.com'
          }
        },
        {
          name: 'CAA',
          value: 9,
          field: {
            name: 'Server Hostname',
            placeholder: 'caa.example.com'
          }
        },
        {
          name: 'PTR',
          value: 10,
          field: {
            name: 'Domain Name',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'SCR',
          value: 11
        },
        {
          name: 'NS',
          value: 12,
          field: {
            name: 'Target',
            placeholder: 'ns1.someserver.com'
          }
        }
      ],
      allTtl: {
        '15': '15s',
        '30': '30s',
        '60': '1m',
        '120': '2m',
        '300': '5m',
        '900': '15m',
        '1800': '30m',
        '3600': '1h',
        '18000': '5h',
        '43200': '12h',
        '86400': '1d'
      },
      awsTypes: [
        'A',
        'AAAA',
        'CNAME',
        'TXT',
        'SRV',
        'MX'
      ],
      form: {
        id: null,
        name: '',
        oldValue: '',
        value: '',
        port: null,
        priority: null,
        weight: 100,
        type: 0,
        ttl: 300,
        flags: 0,
        tag: '',
        monitor_type: 0,
        smart_routing_type: 0,
        enable: true
      }
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    }),
    canUpdateDomainManagement () {
      return this.checkPermissions('domain_management.update')
    },
    awsConnected () {
      return !!this.project.aws_data?.tenant_id
    }
  },
  methods: {
    ...mapActions({
      checkNS: 'domain/checkNS',
      fetchDNS: 'domain/fetchDNS'
    }),
    async fetchDNSRecords () {
      this.loading = true

      const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/records`)
      this.awsDnsMigrated = data.data.find(record => record.Type === 'A')

      if (this.awsDnsMigrated) {
        const records = data.data
          .filter(record => this.awsTypes.includes(record.Type))
          .reduce((acc, record) => {
            let ttl

            if (!['MX', 'SRV', 'TXT'].includes(record.Type)) {
              ttl = record.TTL ? Number(record.TTL) / 60 : undefined
            } else {
              ttl = record.TTL ? Number(record.TTL) : undefined
            }

            const base = {
              Type: record.Type,
              Ttl: ttl,
              Name: record.Name.slice(0, -1),
              solid: record
            }

            if (record.Type === 'MX') {
              record.ResourceRecords.forEach((item, index) => {
                const [Priority, Value] = item.Value.split(' ')
                acc.push({ ...base, Priority, Value, index })
              })
            } else if (record.Type === 'SRV') {
              record.ResourceRecords.forEach((item, index) => {
                const [Priority, Weight, Port, Value] = item.Value.split(' ')
                acc.push({ ...base, Priority, Weight, Port, Value, index })
              })
            } else if (record.Type === 'TXT') {
              record.ResourceRecords.forEach((item, index) => {
                acc.push({ ...base, Value: item.Value.slice(1, -1), index })
              })
            } else {
              let Value
              const first = record.ResourceRecords?.[0]?.Value

              switch (record.Type) {
              case 'A':
                Value = record.AliasTarget ? record.AliasTarget.DNSName : first
                break
              case 'AAAA':
              case 'CNAME':
                Value = first
                break
              default:
                Value = first
              }

              acc.push({ ...base, Value })
            }

            return acc
          }, [])
          .sort((a, b) => {
            let key = this.sortBy
            if (!key) return 0
            if (key === 'TTL') key = 'Ttl'

            let valA = a[key]
            let valB = b[key]

            if (valA == null && valB == null) return 0
            if (valA == null) return this.sortDesc ? 1 : -1
            if (valB == null) return this.sortDesc ? -1 : 1

            if (typeof valA === 'string') valA = valA.toUpperCase()
            if (typeof valB === 'string') valB = valB.toUpperCase()

            return valA < valB ? (this.sortDesc ? 1 : -1) : valA > valB ? (this.sortDesc ? -1 : 1) : 0
          })

        this.totalRecords = records.length
        this.records = this.paginateRecords(records, this.currentPage, this.pageSize)
        this.loading = false
      } else {
        await this.fetchDNS()
          .then((data) => {
            this.totalRecords = data.Records.length
            this.records = this.paginateRecords(data.Records, this.currentPage, this.pageSize)
          })
          .finally(() => this.loading = false)
      }
    },
    async onPageChange (newPage) {
      this.currentPage = newPage
      await this.fetchDNSRecords()
    },
    paginateRecords (records, page, pageSize) {
      const start = (page - 1) * pageSize
      const end = start + pageSize
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return records.slice(start, end)
    },
    async toggleSort (sort) {
      if (this.sortBy === sort) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = sort
        this.sortDesc = false
      }

      await this.fetchDNSRecords()
    },
    editItem (item) {
      this.modal = true
      this.form = {
        id: item.Id,
        name: item.Name,
        oldValue: item.Value,
        value: item.Value,
        port: item.Port,
        priority: item.Priority,
        weight: item.Weight,
        type: item.Type,
        ttl: item.Ttl,
        flags: item.Flags,
        tag: item.Tag,
        monitor_type: item.MonitorType,
        smart_routing_type: item.SmartRoutingType,
        enable: !item.Disabled,
        solid: item.solid,
        index: item.index
      }

      if (this.awsDnsMigrated) {
        if (item.Name === this.project.domain) {
          this.form.name = ''
        } else {
          this.form.name = item.Name.replace(this.project.domain, '').slice(0, -1)
        }
      }
    },
    addItem () {
      this.modal = true
      this.form = {
        id: null,
        name: '',
        oldValue: '',
        value: '',
        port: null,
        priority: null,
        weight: 100,
        type: 0,
        ttl: 300,
        flags: 0,
        tag: '',
        monitor_type: 0,
        smart_routing_type: 0,
        enable: true
      }
    },
    addItemPreset (preset) {
      this.preset = preset
      this.presetModal = true
      this.form = {
        id: null,
        name: '',
        oldValue: '',
        value: '',
        port: null,
        priority: null,
        weight: 100,
        type: 0,
        ttl: 300,
        flags: 0,
        tag: '',
        monitor_type: 0,
        smart_routing_type: 0,
        enable: true
      }

      if (this.preset === 'dmarc') {
        this.form.name = '_dmarc'
        this.form.type = 3
        this.form.value = `v=DMARC1; p=quarantine; rua=mailto:dmarc-reports@${this.project.domain}; sp=none; aspf=r;`
        this.form.rua_email = `dmarc-reports@${this.project.domain}`
      }
    },
    updateRUAEmail (val) {
      this.form.value = `v=DMARC1; p=quarantine; rua=mailto:${val}; sp=none; aspf=r;`
    },
    async addDMARCRecord () {
      this.loading = true
      await axios.post(`api/projects/${this.project.id}/bunny-dns-records`, {
        name: '_dmarc',
        type: 3,
        value: `v=DMARC1; p=quarantine; rua=mailto:dmarc-reports@${this.project.domain}; sp=none; aspf=r;`,
        ttl: 300,
        disabled: false
      })
        .then(({data}) => {
          if (data.data?.ErrorKey === 'validation_error') {
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
          } else {
            this.$emit('success')
            this.modal = false
            this.$swal({
              icon: 'success',
              title: 'DNS Record successfully added',
              showConfirmButton: false,
              timer: 1500
            })
          }
        })
        .catch((err) => {
          console.log(err)
          this.$swal({
            icon: 'error',
            title: 'Server error',
            text: 'Please try again later or let us know about your problem.',
            showConfirmButton: false,
            timer: 3500
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async addRecord () {
      this.loading = true
      let payload = {}

      const regexPublic = /^public\d+\.b-cdn\.net$/
      const regexCommunity = /^community\d+\.b-cdn\.net$/
      // const regexCloudfront = /^[a-z0-9]{10,15}\.cloudfront\.net\.?$/

      // if (regexPublic.test(this.form.value) || regexCommunity.test(this.form.value) || regexCloudfront.test(this.form.value)) {
      if (regexPublic.test(this.form.value) || regexCommunity.test(this.form.value)) {
        this.loading = false
        this.$swal({
          icon: 'error',
          title: 'Validation error',
          text: 'Your community subdomain is already created. You can see it and/or edit it in your domains area.',
          showConfirmButton: false,
          timer: 3500
        })
        return
      }

      if ([0, 1, 2].includes(this.form.type)) {
        payload = {
          weight: Number(this.form.weight),
          monitor_type: this.form.monitor_type,
          smart_routing_type: this.form.smart_routing_type,
          disabled: !this.form.enable
        }
      }

      if ([0, 1, 2].includes(this.form.type) && !this.awsConnected) {
        payload.disabled = !this.form.enable
      }

      if (this.form.type === 4) {
        payload = {
          priority: Number(this.form.priority)
        }
      }

      if (this.form.type === 8) {
        payload = {
          weight: Number(this.form.weight),
          port: Number(this.form.port),
          priority: Number(this.form.priority)
        }
      }

      if (this.form.type === 9) {
        payload = {
          flags: this.form.flags,
          tag: this.form.tag
        }
      }

      let hostname

      if (this.form.name === '@') {
        hostname = ''
      } else {
        hostname = this.form.name
      }

      const endpoint = this.awsConnected
        ? `${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`
        : `api/projects/${this.project.id}/bunny-dns-records`

      await axios.post(endpoint, {
        name: hostname,
        type: this.form.type,
        value: this.form.value,
        ttl: ![5, 11].includes(this.form.ttl) ? this.form.ttl : undefined,
        ...payload
      })
        .then(({data}) => {
          if (data.data?.ErrorKey === 'validation_error') {
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
          } else {
            this.fetchDNSRecords()
            this.modal = false
            this.presetModal = false
            this.$swal({
              icon: 'success',
              title: 'DNS Record successfully added',
              showConfirmButton: false,
              timer: 1500
            })
          }
        })
        .catch((err) => {
          console.log(err)
          this.$swal({
            icon: 'error',
            title: 'Server error',
            text: 'Please try again later or let us know about your problem.',
            showConfirmButton: false,
            timer: 3500
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async deleteRecord (item) {
      this.loading = true
      if (this.awsConnected) {
        const record = item.solid

        const type = this.allTypes.find(type => type.name === record.Type)

        let value
        let priority
        if (record.Type === 'A') {
          value = record.AliasTarget ? record.AliasTarget.DNSName : record.ResourceRecords[0].Value
        } else if (record.Type === 'AAAA') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'CNAME') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'SRV') {
          const index = this.form.index
          value = record.ResourceRecords.reduce((acc, item) => {
            const [Priority, Weight, Port, Value] = item.Value.split(' ')
            acc.push({
              priority: Priority,
              port: Port,
              weight: Weight,
              value: Value
            })
            return acc
          }, [])

          value.splice(index, 1)

          return await axios.put(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: record.TTL ? Number(record.TTL) : 0
          })
            .then(() => {
              this.$swal({
                icon: 'success',
                title: 'DNS Record successfully deleted',
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 3000
              })
              this.fetchDNSRecords()
            })
            .finally(() => {
              this.loading = false
            })
        } else if (record.Type === 'TXT') {
          const index = this.form.index
          value = record.ResourceRecords.reduce((acc, item) => {
            acc.push({value: item.Value.slice(1, -1)})
            return acc
          }, [])
          
          value.splice(index, 1)
          
          return await axios.put(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: record.TTL ? Number(record.TTL) : 0
          })
            .then(() => {
              this.$swal({
                icon: 'success',
                title: 'DNS Record successfully deleted',
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 3000
              })
              this.fetchDNSRecords()
            })
            .finally(() => {
              this.loading = false
            })
        } else if (record.Type === 'MX') {
          const index = this.form.index
          value = record.ResourceRecords.reduce((acc, item) => {
            const [Priority, Value] = item.Value.split(' ')
            acc.push({
              priority: Number(Priority),
              value: Value
            })
            return acc
          }, [])

          value.splice(index, 1)

          return await axios.put(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: record.TTL ? Number(record.TTL) : 0
          })
            .then(() => {
              this.$swal({
                icon: 'success',
                title: 'DNS Record successfully deleted',
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 3000
              })
              this.fetchDNSRecords()
            })
            .finally(() => {
              this.loading = false
            })
        }

        await axios.delete(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
          data: {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: Number(record.TTL),
            priority: priority
          }
        })
          .then(() => {
            this.$swal({
              icon: 'success',
              title: 'DNS Record successfully deleted',
              toast: true,
              position: 'top-right',
              showConfirmButton: false,
              timer: 3000
            })
            this.fetchDNSRecords()
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        await axios.delete(`api/projects/${this.project.id}/bunny-dns/${item.Id}`)
          .then(() => {
            this.$swal({
              icon: 'success',
              title: 'DNS Record successfully deleted',
              toast: true,
              position: 'top-right',
              showConfirmButton: false,
              timer: 3000
            })
            this.fetchDNSRecords()
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async updateRecord () {
      // if (!this.form.id) return
      this.loading = true
      let payload = {}

      const regexPublic = /^public\d+\.b-cdn\.net$/
      const regexCommunity = /^community\d+\.b-cdn\.net$/

      if (this.form.oldValue !== this.form.value && (regexPublic.test(this.form.value) || regexCommunity.test(this.form.value))) {
        this.loading = false
        this.$swal({
          icon: 'error',
          title: 'Validation error',
          text: 'This target is reserved for Estage',
          showConfirmButton: false,
          timer: 3500
        })
        return
      }

      if ([0, 1, 2].includes(this.form.type)) {
        payload = {
          weight: this.form.weight,
          monitor_type: this.form.monitor_type,
          smart_routing_type: this.form.smart_routing_type,
          disabled: !this.form.enable
        }
      }

      if (this.form.type === 4) {
        payload = {
          priority: this.form.priority
        }
      }
      
      if (this.form.type === 8) {
        payload = {
          weight: this.form.weight,
          port: this.form.port,
          priority: this.form.priority
        }
      }

      if (this.form.type === 9) {
        payload = {
          flags: this.form.flags,
          tag: this.form.tag
        }
      }

      if (this.awsDnsMigrated) {
        const record = this.form.solid

        const type = this.allTypes.find(type => type.name === record.Type)

        let value
        let priority
        if (record.Type === 'A') {
          value = record.AliasTarget ? record.AliasTarget.DNSName : record.ResourceRecords[0].Value
        } else if (record.Type === 'AAAA') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'CNAME') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'SRV') {
          const index = this.form.index
          value = record.ResourceRecords.reduce((acc, item) => {
            const [Priority, Weight, Port, Value] = item.Value.split(' ')
            acc.push({
              priority: Priority,
              port: Port,
              weight: Weight,
              value: Value
            })
            return acc
          }, [])

          value[index].priority = this.form.priority
          value[index].port = this.form.port
          value[index].weight = this.form.weight
          value[index].value = this.form.value

          let hostname

          if (record.Name === '@') {
            hostname = ''
          } else {
            hostname = record.Name
          }

          let ttl

          if (!this.form.ttl) {
            ttl = undefined
          } else {
            ttl = Number(this.form.ttl)
          }

          return await axios.put(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
            name: hostname,
            type: type.value,
            value: value,
            ttl: ttl
          })
            .then(({data}) => {
              if (data.data?.ErrorKey === 'validation_error') {
                this.$swal({
                  icon: 'error',
                  title: 'Server error',
                  text: 'Please try again later or let us know about your problem.',
                  showConfirmButton: false,
                  timer: 3500
                })
              } else {
                this.fetchDNSRecords()
                this.modal = false
                this.$swal({
                  icon: 'success',
                  title: 'DNS Record successfully updated',
                  toast: true,
                  position: 'top-right',
                  showConfirmButton: false,
                  timer: 1500
                })
              }
            })
            .catch((err) => {
              console.log(err)
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            })
            .finally(() => {
              this.loading = false
            })
        } else if (record.Type === 'TXT') {
          value = record.ResourceRecords[0].Value.slice(1, -1)
          const index = this.form.index
          value = record.ResourceRecords.reduce((acc, item) => {
            acc.push({value: item.Value.slice(1, -1)})
            return acc
          }, [])
          
          value[index].value = this.form.value

          let hostname

          if (record.Name === '@') {
            hostname = ''
          } else {
            hostname = record.Name
          }

          let ttl

          if (!this.form.ttl) {
            ttl = undefined
          } else {
            ttl = Number(this.form.ttl)
          }

          return await axios.put(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
            name: hostname,
            type: type.value,
            value: value,
            ttl: ttl
          })
            .then(({data}) => {
              if (data.data?.ErrorKey === 'validation_error') {
                this.$swal({
                  icon: 'error',
                  title: 'Server error',
                  text: 'Please try again later or let us know about your problem.',
                  showConfirmButton: false,
                  timer: 3500
                })
              } else {
                this.fetchDNSRecords()
                this.modal = false
                this.$swal({
                  icon: 'success',
                  title: 'DNS Record successfully updated',
                  toast: true,
                  position: 'top-right',
                  showConfirmButton: false,
                  timer: 1500
                })
              }
            })
            .catch((err) => {
              console.log(err)
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            })
            .finally(() => {
              this.loading = false
            })
        } else if (record.Type === 'MX') {
          const index = this.form.index
          value = record.ResourceRecords.reduce((acc, item) => {
            const [Priority, Value] = item.Value.split(' ')
            acc.push({
              priority: Number(Priority),
              value: Value
            })
            return acc
          }, [])

          value[index].priority = Number(this.form.priority)
          value[index].value = this.form.value

          let hostname

          if (record.Name === '@') {
            hostname = ''
          } else {
            hostname = record.Name
          }

          let ttl

          if (!this.form.ttl) {
            ttl = undefined
          } else {
            ttl = Number(this.form.ttl)
          }

          return await axios.put(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
            name: hostname,
            type: type.value,
            value: value,
            ttl: ttl
          })
            .then(({data}) => {
              if (data.data?.ErrorKey === 'validation_error') {
                this.$swal({
                  icon: 'error',
                  title: 'Server error',
                  text: 'Please try again later or let us know about your problem.',
                  showConfirmButton: false,
                  timer: 3500
                })
              } else {
                this.fetchDNSRecords()
                this.modal = false
                this.$swal({
                  icon: 'success',
                  title: 'DNS Record successfully updated',
                  toast: true,
                  position: 'top-right',
                  showConfirmButton: false,
                  timer: 1500
                })
              }
            })
            .catch((err) => {
              console.log(err)
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            })
            .finally(() => {
              this.loading = false
            })
        }

        await axios.delete(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
          data: {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: record.TTL ? Number(record.TTL) : 0,
            priority: priority
          }
        })

        let recordType
        if (typeof this.form.type === 'string') {
          const val = this.allTypes.find(item => item.name === this.form.type)
          recordType = val?.value
        } else {
          const val = this.allTypes.find(item => item.value === this.form.type)
          recordType = val?.value
        }

        let name = this.form.name ? this.form.name + '.' + this.project.domain + '.' : this.project.domain + '.'

        await axios.post(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
          name: name,
          type: recordType,
          value: this.form.value,
          ttl: this.form.ttl || 0,
          priority: this.form.priority
        })
          .then(({data}) => {
            if (data.data?.ErrorKey === 'validation_error') {
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            } else {
              this.fetchDNSRecords()
              this.modal = false
              this.$swal({
                icon: 'success',
                title: 'DNS Record successfully updated',
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 1500
              })
            }
          })
          .catch((err) => {
            console.log(err)
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        await axios.put(`api/projects/${this.project.id}/bunny-dns-records/${this.form.id}`, {
          name: this.form.name,
          type: this.form.type,
          value: this.form.value,
          ttl: ![5, 11].includes(this.form.ttl) ? this.form.ttl : undefined,
          disabled: [0, 1, 2].includes(this.form.type) ? !this.form.enable : false,
          ...payload
        })
          .then(({data}) => {
            if (data.data?.ErrorKey === 'validation_error') {
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            } else {
              this.fetchDNSRecords()
              this.modal = false
              this.$swal({
                icon: 'success',
                title: 'DNS Record successfully updated',
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 1500
              })
            }
          })
          .catch((err) => {
            console.log(err)
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
          })
          .finally(() => {
            this.loading = false
          }) 
      }
    }
  },
  async mounted () {
    await this.$store.dispatch('projects/hasAccessToPage', 'domain_management')
    await this.fetchDNSRecords()
  }
}
</script>

<style lang="scss">
.input-hostname .input-group-text {
  background: transparent;
  height: 40px;
}
</style>